<script setup lang="ts">
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import type { ImageNode } from '@/lib/shopify/product.ts';
import { watchOnce } from '@vueuse/core';
import { ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    images: ImageNode[];
    modelValue?: number;
  }>(),
  {
    modelValue: 0,
  },
);

const emit = defineEmits<{ 'update:modelValue': [number]; click: [number] }>();

const api = ref<CarouselApi>();

const selectedIndex = ref(0);

function setApi(val: CarouselApi) {
  api.value = val;
}

watchOnce(api, () => {
  if (!api.value) {
    return;
  }

  api.value.on('select', (api) => {
    selectedIndex.value = api.selectedScrollSnap();

    if (props.modelValue !== selectedIndex.value) {
      emit('update:modelValue', selectedIndex.value);
    }
  });

  selectedIndex.value = api.value.selectedScrollSnap();
});

function select(i: number) {
  if (api.value) {
    api.value.scrollTo(i);
  }
}

watch(
  () => props.modelValue,
  (val) => {
    if (api.value && val !== undefined && val !== selectedIndex.value) {
      api.value.scrollTo(val);
    }
  },
);

function imageClicked() {
  emit('click', selectedIndex.value);
}
</script>

<template>
  <Carousel class="carousel" :opts="{ loop: true, startIndex: modelValue }" @init-api="setApi">
    <CarouselContent class="cursor-pointer" @click="imageClicked">
      <CarouselItem v-for="image in images" class="h-full w-full flex items-center justify-center">
        <img class="h-full rounded-xl bg-primary" :src="image.url" :alt="image.altText ?? 'Product image'" />
      </CarouselItem>
      <CarouselItem v-if="images.length === 0">
        <div class="rounded-xl bg-neutral-300 min-h-[300px] flex items-center justify-center select-none">
          <p class="text-center text-neutral-600 font-semibold">No images available.</p>
        </div>
      </CarouselItem>
    </CarouselContent>
    <CarouselPrevious />
    <CarouselNext />

    <div class="dots">
      <div v-for="(_, i) in images" :data-active="selectedIndex === i" @click="select(i)"></div>
    </div>
  </Carousel>
</template>

<style>
.carousel .dots {
  @apply absolute bottom-2 left-0 right-0 flex items-center justify-center;

  > div {
    @apply relative w-4 h-4 cursor-pointer;

    &::after {
      @apply absolute w-1 h-1 rounded-full bg-neutral-400 mix-blend-difference;
      content: '';
    }

    &[data-active='false']::after {
      @apply left-[6px] top-[6px];
    }

    &[data-active='true']::after {
      @apply w-2 h-2 left-[4px] top-[4px];
    }
  }
}
</style>
